/** @format */

import {format, subDays} from 'date-fns';

import {safeDivide} from 'helpers/numberHelpers';

import {FunnelGoalStatFromQuery} from '../../../models/api';
import {GraphComplexValuesList} from '../../../models/common';

export interface MixedCrStats {
  yesterday: number;
  min: number;
  max: number;
  dynamics: number;
}

const computeMixedCrStats = (
  graphComplexValues: GraphComplexValuesList,
  funnelGoalStats: ReadonlyArray<FunnelGoalStatFromQuery>,
  numeratorKey: 'clicks' | 'impressions' | number,
  denominatorKey: 'clicks' | 'impressions' | number,
): MixedCrStats => {
  const pointNames = Object.keys(graphComplexValues).sort();
  const crSeries: number[] = [];
  const dateToCr = new Map<string, number>();

  pointNames.forEach(pointName => {
    const data = graphComplexValues[pointName];
    if (!data) return;

    let numerator: number;
    let denominator: number;

    // Получаем числитель
    if (typeof numeratorKey === 'string') {
      numerator = data.values[numeratorKey] ?? 0;
    } else {
      // Ищем в funnelGoalStats по goalId
      const date = data.date;
      const goalStats = funnelGoalStats.filter(s => s.goalId === numeratorKey && s.date === date);
      numerator = goalStats.reduce((sum, s) => sum + (s.conversions || 0), 0);
    }

    // Получаем знаменатель
    if (typeof denominatorKey === 'string') {
      denominator = data.values[denominatorKey] ?? 0;
    } else {
      // Ищем в funnelGoalStats по goalId
      const date = data.date;
      const goalStats = funnelGoalStats.filter(s => s.goalId === denominatorKey && s.date === date);
      denominator = goalStats.reduce((sum, s) => sum + (s.conversions || 0), 0);
    }

    // Исключаем дни без данных/нулевого знаменателя из ряда статов
    if (denominator && denominator > 0) {
      const cr = safeDivide(numerator, denominator) * 100;
      crSeries.push(cr);
      dateToCr.set(data.date, cr);
    } else {
      crSeries.push(Number.NaN);
    }
  });

  const valid = crSeries.filter(v => Number.isFinite(v));
  if (!valid.length) {
    // eslint-disable-next-line no-console
    console.warn('[CR] computeMixedCrStats: пустой ряд CR', {
      numeratorKey,
      denominatorKey,
      pointsCount: pointNames.length,
    });
    const yesterday = 0;
    return {yesterday, min: 0, max: 0, dynamics: 0};
  }

  // Вчера строго по календарной дате (ISO)
  const yesterdayIso = format(subDays(new Date(), 1), 'yyyy-MM-dd');
  const yesterdayRaw = dateToCr.get(yesterdayIso);
  const yesterday = Number.isFinite(yesterdayRaw as number) ? ((yesterdayRaw as number) ?? 0) : 0;
  const min = Math.min(...valid);
  const max = Math.max(...valid);
  const first = valid.find(v => Number.isFinite(v)) || 0;
  const dynamics = yesterday - first;

  return {yesterday, min, max, dynamics};
};

export default computeMixedCrStats;
